import { FC, PropsWithChildren, useEffect, useRef } from "react";
import useAuth from "./hooks/useAuth";
import { useCookies } from "@/hooks/useCookies";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import useOpenId from "./hooks/useOpenId";
import useToken from "./hooks/useToken";
import useClientId from "./hooks/useClientId";
import { useSearchParams } from "react-router";
import { useAppSelector } from "@/hooks";

interface AuthChecker extends PropsWithChildren {}

// This provider checks the url and the current cookies for authentication
export const AuthChecker: FC<AuthChecker> = ({
  children
}) => {

  const isAuthenticated = useAppSelector(store => store.auth.isAuthenticated);
  
  const [searchParams, _setSearchParams] = useSearchParams();
  const hasChecked = useRef(false);

  const Settings = useSettingsContext();  
  const { loginNotMade, loginRefresh } = useAuth();
  const { loginToken } = useToken();
  const { loginClientId } = useClientId();
  const { loginOpenId } = useOpenId();
  const { getToken } = useCookies();
  const cookiesToken = getToken();

  useEffect(() => {
    if (!hasChecked.current) {
      hasChecked.current = true;

      // Token searchParams
      const token = searchParams.get("token");
      if ( token ) {
        loginToken({token});
        return;
      }
      
      // ClientID & Secret searchParams
      const clientId = searchParams.get("id_client");
      const secret = searchParams.get("secret");
      if ( clientId && secret ) {
        loginClientId({clientId, secret});
        return;
      }
      
      // Already logged in with stored session token
      // isAuthenticated is checked to avoid the private settings fetch rerender
      if ( cookiesToken && !isAuthenticated) {
        loginRefresh(cookiesToken);
        return;
      }

      // Code, Nonce & Secret searchParams
      if ( Settings.features?.oidc) {
        
        const code = searchParams.get("code")!;
        const state = searchParams.get("state")!;
        if (code && state) {
          loginOpenId({code, state});
          return;
        }

      }

      if (
        // Waiting for the login request to finish in the second rerender
        //   !(path == "common:url.connect" && (searchParams.get("waiting") === "true"))
        !isAuthenticated
      ) {
        loginNotMade();
        return;
      }

    }
  }, [searchParams, isAuthenticated]);

  return children;
}